import { useState } from "react";
import { useQueryClient } from "react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoaderButton } from "@/components/LoaderButton";
import { useToast } from "@/hooks/use-toast";
import { useBffAxios } from "@/context/axios/AxiosContext";
import { useAuthUser } from "@/context/auth/AuthContext";
import { cargarMiPin, problemaDelPin } from "@/context/caja/pinDelCajero";
import { pos } from "@/platform";
import type { PosLocalTerminal } from "@/platform/types";
import { MANDATO_QUERY_KEY } from "@/hooks/react-query/Caja/useMandatoDeLaCaja";

/**
 * El cajero carga o cambia su PIN, con red y con su sesión (#346 AC-2; §2.6).
 * Con él entra sin red a esta caja. El PIN no sale del equipo.
 */
function CargarMiPin({
  terminal,
  onClose,
}: {
  terminal: PosLocalTerminal;
  onClose: () => void;
}) {
  const axios = useBffAxios();
  const qc = useQueryClient();
  const user = useAuthUser();
  const { toast } = useToast();
  const [pin, setPin] = useState("");
  const [repetido, setRepetido] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  const cargar = async () => {
    const invalido = problemaDelPin(pin, repetido);
    if (invalido) {
      setProblema(invalido);
      return;
    }
    setEnviando(true);
    setProblema(null);
    try {
      const resultado = await cargarMiPin(
        { pos, post: (url, body) => axios.post(url, body) },
        {
          businessId: terminal.businessId,
          terminalId: terminal.terminalId as string,
          installationId: terminal.installationId,
          userId: String(user?.id ?? ""),
          nombre:
            [user?.name, user?.lastName].filter(Boolean).join(" ").trim() ||
            "Cajero",
          pin,
        }
      );
      if (resultado.tipo === "ok") {
        await qc.invalidateQueries(MANDATO_QUERY_KEY);
        toast({
          title: "PIN cargado",
          description: `Ya podés entrar con tu PIN a «${terminal.name}» sin conexión.`,
          variant: "success",
          duration: 5000,
        });
        onClose();
        return;
      }
      setProblema(
        resultado.tipo === "sin-respuesta"
          ? "No hubo respuesta del servidor. El PIN se carga con conexión: probá de nuevo."
          : resultado.mensaje
      );
    } catch (error) {
      setProblema(
        error instanceof Error && error.message
          ? error.message
          : "No se pudo guardar el PIN en este equipo."
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && !enviando && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cargar mi PIN</DialogTitle>
          <DialogDescription className="text-pretty">
            Con tu PIN entrás a esta caja y vendés aunque no haya conexión. Son
            de 4 a 8 números, y quedan sólo en este equipo.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="pin">PIN</Label>
            <Input
              id="pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="pin-repetido">Repetí el PIN</Label>
            <Input
              id="pin-repetido"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={repetido}
              onChange={(e) => setRepetido(e.target.value)}
            />
          </div>
          {problema && (
            <p role="alert" className="text-sm text-error-foreground">
              {problema}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <LoaderButton isLoading={enviando} onClick={() => void cargar()}>
            Guardar mi PIN
          </LoaderButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CargarMiPin;
